import * as React from 'react';
import { Formik } from 'formik';
import { FormattedMessage } from 'react-intl';
import {
  TableRow,
  TableCell,
  Button,
  Typography,
  Grid,
  Tooltip,
} from '@material-ui/core';
import moment from 'moment';
import NoRecords from '../Shared/NoRecords';
import ContainerCard from '../Shared/container-card';
import ListTable from '../Shared/list-table';
import { Input, Select } from '../Shared/BsInput';
import { useHistory } from 'react-router-dom';
import ErrorsModal from '../../containers/Queues/errors';
import {
  QUEUE_VIEWS,
  DEFAULT_QUEUE_VIEW,
  queueStatusLabelId,
} from '../../helpers/queue-filters';

const DATE_FORMAT = 'DD-MM-YYYY HH:mm';

const formatDate = date => (date ? moment(date).format(DATE_FORMAT) : '-');

const QueueStatus = ({ status }) => {
  const labelId = queueStatusLabelId(status);

  return labelId ? <FormattedMessage id={labelId} /> : '-';
};

const Validity = ({ meeting }) =>
  meeting
    ? `${formatDate(meeting.date_from)} – ${formatDate(meeting.date_to)}`
    : '-';

const ActionIcon = ({ titleId, icon, color, onClick }) => (
  <Tooltip title={<FormattedMessage id={titleId} />}>
    <i
      className={`fa ${icon}`}
      style={{ color, cursor: 'pointer' }}
      onClick={onClick}
    ></i>
  </Tooltip>
);

const RenderTable = ({
  onPageChange,
  pages,
  page,
  queues,
  retry,
  destroy,
  setInfoOpenModal,
}) => {
  const history = useHistory();

  return (
    <ListTable
      onPageChange={onPageChange}
      pages={pages}
      page={page}
      rowsPerPage={20}
      head={
        <TableRow>
          <TableCell>
            <FormattedMessage id="ID" />
          </TableCell>
          <TableCell>
            <FormattedMessage id="Invitation ID" />
          </TableCell>
          <TableCell>
            <FormattedMessage id="Status" />
          </TableCell>
          <TableCell>
            <FormattedMessage id="Building" />
          </TableCell>
          <TableCell>
            <FormattedMessage id="Validity" />
          </TableCell>
          <TableCell>
            <FormattedMessage id="Date" />
          </TableCell>
          <TableCell>
            <FormattedMessage id="Attempts" />
          </TableCell>
          <TableCell></TableCell>
        </TableRow>
      }
      body={
        <React.Fragment>
          {queues.map(queue => {
            const meeting = queue.Invitation?.Meeting;
            const meetingId = queue.Invitation?.meeting_id;

            return (
              <TableRow key={queue.id}>
                <TableCell>{queue.id}</TableCell>
                <TableCell>{queue.invitation_id}</TableCell>
                <TableCell>
                  <QueueStatus status={queue.queue_status} />
                </TableCell>
                <TableCell>{meeting?.Building?.name || '-'}</TableCell>
                <TableCell>
                  <Validity meeting={meeting} />
                </TableCell>
                <TableCell>{formatDate(queue.date)}</TableCell>
                <TableCell>{queue.attempts}</TableCell>
                <TableCell>
                  <div style={{ display: 'flex', columnGap: '10px' }}>
                    {!!meetingId && (
                      <ActionIcon
                        titleId="Go to invite"
                        icon="fa-eye"
                        color="blue"
                        onClick={() =>
                          history.push(`/app/meetings/${meetingId}`)
                        }
                      />
                    )}

                    {!!queue.can_retry && (
                      <ActionIcon
                        titleId="Retry"
                        icon="fa-rotate-right"
                        color="green"
                        onClick={() => retry(queue)}
                      />
                    )}

                    <ActionIcon
                      titleId="View errors log"
                      icon="fa-file-text-o"
                      color="orange"
                      onClick={() =>
                        setInfoOpenModal(queue.id, queue.invitation_id)
                      }
                    />

                    {!!queue.pending && (
                      <ActionIcon
                        titleId="Cancel"
                        icon="fa-trash"
                        color="red"
                        onClick={() => destroy(queue.id)}
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </React.Fragment>
      }
    />
  );
};

const VIEW_OPTIONS = [
  { labelId: 'Open (not expired)', value: QUEUE_VIEWS.OPEN },
  { labelId: 'Pending (all)', value: QUEUE_VIEWS.PENDING },
  { labelId: 'Closed', value: QUEUE_VIEWS.CLOSED },
  { labelId: 'All', value: QUEUE_VIEWS.ALL },
].map(({ labelId, value }) => ({
  label: <FormattedMessage id={labelId} />,
  value,
}));

const RenderForm = ({
  values,
  errors,
  handleChange,
  handleSubmit,
  isSubmitting,
}) => {
  return (
    <form onSubmit={handleSubmit} autoComplete="off">
      <Grid container spacing={3}>
        <Grid item xs={6} md={3}>
          <Input
            type="text"
            value={values.id}
            onChange={handleChange}
            name="id"
            label={<FormattedMessage id="ID" />}
            errors={errors}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Input
            type="text"
            value={values.invitation_id}
            onChange={handleChange}
            name="invitation_id"
            label={<FormattedMessage id="Invitation ID" />}
            errors={errors}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Select
            value={values.view}
            onChange={handleChange}
            name="view"
            label={<FormattedMessage id="Situation" />}
            errors={errors}
            options={VIEW_OPTIONS}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={isSubmitting}
            fullWidth
          >
            <FormattedMessage id="Filter" />
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default ({ onPageChange, pages, page, queues, retry, destroy }) => {
  const [openModal, setOpenModal] = React.useState(false);
  const [infosModal, setInfosModal] = React.useState({
    queue_id: null,
    invitation_id: null,
  });

  const setInfoOpenModal = (queue_id, invitation_id) => {
    setInfosModal({ queue_id, invitation_id });
    setOpenModal(true);
  };

  return (
    <React.Fragment>
      <ContainerCard
        xs={12}
        md={12}
        header={
          <Typography variant="h6">
            <i className="fa fa-list"></i>{' '}
            <FormattedMessage id="Access codes queue" />
          </Typography>
        }
        content={
          <React.Fragment>
            <Formik
              initialValues={{
                id: '',
                invitation_id: '',
                view: DEFAULT_QUEUE_VIEW,
                order: 'date DESC',
              }}
              enableReinitialize={true}
              onSubmit={async (values, { setSubmitting, setErrors }) => {
                try {
                  await onPageChange({ page: 1, ...values });
                  setSubmitting(false);
                } catch (err) {
                  if (err.response && err.response.data.errors) {
                    setErrors(err.response.data.errors);
                  }

                  setSubmitting(false);
                }
              }}
              render={RenderForm}
            />
            <hr />
            {!queues.length && <NoRecords />}
            {!!queues.length && (
              <RenderTable
                {...{
                  onPageChange,
                  pages,
                  page,
                  queues,
                  retry,
                  destroy,
                  setInfoOpenModal,
                }}
              />
            )}
            {openModal && (
              <ErrorsModal setOpenModal={setOpenModal} infos={infosModal} />
            )}
          </React.Fragment>
        }
      />
    </React.Fragment>
  );
};
